import type { Identity } from '@cc-shopping/platform-analytics/IdentityContext/identityService';
import { InfoTooltip } from '@cc-shopping/ui/composites/InfoTooltip';
import { isNil } from '@cc-shopping/utils/object/isNil';
import { Box, Typography } from '@mui/material';
import type { ApprovalOddsAnalytics, Odds } from '.';
import { ApprovalLabel } from './ApprovalLabel';
import { ApprovalLine } from './ApprovalLine';
import { ApprovalOddsButton } from './ApprovalOddsButton';
import { TransUnionLink } from './TransUnionLink';
import { getLabelEndColumn, getLabelStartColumn, getOddsLabel } from './utils';
type ApprovalOddsGraphContentProps = {
  identity: Identity | undefined;
  odds: Odds;
  userCreditScore: number | undefined;
  creditScalar: number;
  isBelowMin: boolean;
  analytics?: ApprovalOddsAnalytics;
  hideOddsButton?: boolean;
  largeText?: boolean;
  hasLimitedCreditHistory?: boolean;
  hideTooltip?: boolean;
  adjustOddsButton?: boolean;
  firePageActionOnClick?: boolean;
  hideOddsButtonArrow?: boolean;
  smallApprovalLabelText?: boolean;
  smallScoreRangeText?: boolean;
  isAmexOffer?: boolean;
};
export const ApprovalOddsGraphContent = ({
  analytics,
  identity,
  odds,
  userCreditScore,
  creditScalar,
  isBelowMin,
  hideOddsButtonArrow = true,
  hideOddsButton = false,
  hideTooltip = false,
  adjustOddsButton = false,
  firePageActionOnClick,
  smallApprovalLabelText = false,
  smallScoreRangeText = false,
  isAmexOffer
}: ApprovalOddsGraphContentProps) => {
  const {
    min,
    max
  } = odds;
  const labelStart = getLabelStartColumn(min);
  const labelEnd = getLabelEndColumn(max);
  const label = getOddsLabel(min, max);
  const isCreditScoreDefined = !isNil(userCreditScore);
  return <Box position="relative">
      {isCreditScoreDefined && <>
          <Box sx={{
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        backgroundColor: isBelowMin ? 'reds.main' : 'primary.main',
        position: 'absolute',
        top: '23px',
        left: `calc(${userCreditScore * creditScalar * 100}%)`
      }} />
          {identity?.creditScore && <Box sx={{
        position: 'absolute',
        left: `calc(${userCreditScore * creditScalar * 100}% - 16px)`,
        top: '40px'
      }}>
              <Typography variant="textSmall" sx={theme => ({
          backgroundColor: isBelowMin ? theme.palette.reds.main : theme.palette.primary.main,
          padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
          borderRadius: '2px',
          color: theme.palette.common.white,
          fontSize: {
            xs: '0.625rem',
            md: '.813rem'
          }
        })}>
                {identity.creditScore}
              </Typography>
              {!hideTooltip && <InfoTooltip ariaLabel="More information about your Credit Score" text={<>
                      {!isAmexOffer ? <>
                          <Typography>
                            You&apos;re{' '}
                            <Typography component="span" fontWeight={700}>
                              {Math.abs(identity.creditScore - odds.min)} points
                            </Typography>{' '}
                            {identity.creditScore < odds.min ? 'below' : 'above'} the lowest
                            suggested score of{' '}
                            <Typography component="span" fontWeight={700}>
                              {odds.min}
                            </Typography>
                            .
                          </Typography>
                        </> : <Typography>
                          Your credit score of{' '}
                          <Typography component="span" fontWeight={700}>
                            {identity.creditScore}
                          </Typography>
                          {' is '}
                          {identity.creditScore < odds.min ? 'not within' : 'within'} the
                          recommended score for this card.
                        </Typography>}
                      <Typography>
                        But, heads up, being within the recommended range doesn&apos;t guarantee
                        approval. Credit score is only one factor lenders consider and they may not
                        use the TransUnion Vantage Score like we do.
                      </Typography>
                    </>} />}
            </Box>}
        </>}
      <Box sx={theme_0 => ({
      display: 'grid',
      gridTemplateRows: 'repeat(3, auto)',
      gridTemplateColumns: 'repeat(4, 1fr)',
      columnGap: theme_0.spacing(1),
      marginBottom: isCreditScoreDefined ? 5 : 3
    })}>
        <ApprovalLine labelStart={labelStart} labelEnd={labelEnd} hasCreditScore={userCreditScore !== undefined} odds={odds} isBelowMin={isBelowMin} smallText={smallScoreRangeText} isAmexOffer={isAmexOffer} />
        {!userCreditScore && <Box display="flex" fontSize="1rem" justifyContent={{
        xs: labelStart <= 2 ? 'flex-start' : 'flex-end',
        sm: 'flex-start'
      }} overflow="visible" gridRow={3} position="relative" height="1.75rem" sx={{
        gridColumnStart: labelStart,
        gridColumnEnd: labelEnd
      }}>
            <ApprovalLabel hasCreditScore={userCreditScore !== undefined} smallText={smallApprovalLabelText} additionalStyles={{
          position: 'absolute',
          bottom: 0
        }}>
              {label}
            </ApprovalLabel>
          </Box>}
      </Box>

      {!isCreditScoreDefined && !hideOddsButton && <Box display="flex" sx={theme_1 => ({
      marginTop: adjustOddsButton ? '-1rem' : theme_1.spacing(3.5),
      [theme_1.breakpoints.down('md')]: {
        marginTop: adjustOddsButton ? '-1rem' : theme_1.spacing(1)
      }
    })}>
          <ApprovalOddsButton analytics={analytics} hideTooltip={hideTooltip} firePageActionOnClick={firePageActionOnClick} />
        </Box>}
      {isCreditScoreDefined && <TransUnionLink hideOddsButtonArrow={hideOddsButtonArrow} />}
    </Box>;
};